// src/App.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ThemeSwitcherProvider } from 'react-css-theme-switcher';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ConfigProvider, theme as antdTheme, App as AntdApp, Watermark } from 'antd';
import { css, Global } from '@emotion/react';

import Layouts from 'layouts/Main/LayoutIndex';
import { applyCustomStyle } from 'configs/ApplicationStyles';
import { darkTheme, lightTheme, baseTheme } from 'configs/appEngine';
import app from 'configs/appResolver';
import useZustand from 'hooks/useZustand';
import useBodyClass from 'hooks/useBodyClass';
import i18n, { resources } from 'lang';
import { languageContextReload } from 'lang/languageContextReload';
import SimpleLoadingApp from 'components/SimpleComponents/SimpleLoadingApp';
import { SettingsDrawer } from 'layouts/TopBar/ApplicationSettings';
import BottomDrawer from 'layouts/BottomDrawer';
import PwaInstallPrompt from 'components/core/PwaInstallPrompt';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const themes = {
  dark: `/css/dark-theme.css`,
  light: `/css/light-theme.css`,
};

const resFromCanon = (canon = '') => {
  const v = canon.toLowerCase();
  if (v.startsWith('pt')) return 'pt';
  if (v.startsWith('en')) return 'en';
  if (v.startsWith('zh')) return 'zh';
  if (v.startsWith('fr')) return 'fr';
  if (v.startsWith('ja')) return 'ja';
  return 'en';
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const App = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedLanguage = useZustand('theme', 'language');
  const setLanguageStore = useZustand('theme', 'setLanguage');
  const storedToken = useZustand('auth', 'token');

  const blankLayout = useZustand('theme', 'blankLayout');
  const direction = useZustand('theme', 'direction');
  const currentTheme = useZustand('theme', 'currentTheme');
  const appName = app.NAME || 'Jarvisly App';
  const appId = app.ID || 'jarvisly';
  const basePath = useZustand('theme', 'basePath');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = antdTheme.useToken();
  useBodyClass();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // const storedCurrentTheme = useZustand('theme', 'currentTheme');
  // const storedWatermark = useZustand('theme', 'watermark');
  const currentAppLocale = resources[resFromCanon(storedLanguage)] || resources.en;
  const themeConfig = currentTheme === 'light' ? { ...lightTheme } : { ...darkTheme };

  // Dynamic PWA manifest — adapts name per environment (production vs homologation)
  const manifestUrl = useMemo(() => {
    // Blob URLs have no origin context, so the browser cannot resolve relative paths
    // like '/' or '/apps/...'. All URLs in a blob manifest must be absolute.
    const origin = window.location.origin;

    const manifest = {
      name: app.PWA_NAME || app.NAME,
      short_name: app.PWA_SHORT_NAME || app.NAME,
      description: app.PWA_DESCRIPTION || '',
      start_url: `${origin}/`,
      scope: `${origin}/`,
      display: 'standalone',
      orientation: 'portrait',
      theme_color: baseTheme?.colorPrimary || '#000000',
      background_color: '#ffffff',
      icons: [
        {
          src: `${origin}/apps/${appId}/web-app-manifest-192x192.png`,
          sizes: '192x192',
          type: 'image/png',
          purpose: 'any maskable',
        },
        {
          src: `${origin}/apps/${appId}/web-app-manifest-512x512.png`,
          sizes: '512x512',
          type: 'image/png',
          purpose: 'any maskable',
        },
      ],
    };
    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
    return URL.createObjectURL(blob);
  }, [appId]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [languageVersion, setLanguageVersion] = useState(0);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    applyCustomStyle(currentTheme);
  }, [currentTheme]);

  // Add/remove body-dark-app class based on theme
  useEffect(() => {
    if (currentTheme === 'dark') {
      document.body.classList.add('body-dark-app');
    } else {
      document.body.classList.remove('body-dark-app');
    }
  }, [currentTheme]);

  // Set CSS variable for primary color (used by scrollbar and other elements)
  useEffect(() => {
    // Use baseTheme.colorPrimary directly as it's the app's configured primary color
    if (baseTheme?.colorPrimary) {
      document.documentElement.style.setProperty('--primary-color', baseTheme.colorPrimary);
    }
  }, []);

  useEffect(() => {
    // Listen to i18n storedLanguage changes and increment version counter
    // Components can subscribe to LanguageContext to re-render when storedLanguage changes
    const handleLanguageChange = () => {
      setLanguageVersion(prev => prev + 1);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // Set app-specific favicon via direct DOM manipulation (more reliable than Helmet for browsers that
  // cache favicons aggressively and don't pick up async head updates from react-helmet-async)
  useEffect(() => {
    const faviconHref = `/apps/${appId}/favicon.ico`;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = 'image/x-icon';
    link.href = faviconHref;
  }, [appId]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Settings Drawer - Outside of ConfigProvider to prevent remounting on theme change */}
      <SettingsDrawer />

      <languageContextReload.Provider value={languageVersion}>
        <ConfigProvider theme={themeConfig} direction={direction} locale={currentAppLocale.antd}>
          <AntdApp>
            {/*<Watermark*/}
            {/*  content={storedWatermark?.content}*/}
            {/*  font={{*/}
            {/*    color: storedCurrentTheme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',*/}
            {/*  }}*/}
            {/*  zIndex={0}*/}
            {/*>*/}
            <Global
              styles={css`
                body {
                  background-color: ${currentTheme === 'dark' ? '#1B2531' : '#FAFAFB'};
                  overflow-x: hidden;
                }

                a:not(.ant-pagination a) {
                  color: ${baseTheme.colorLink} !important;
                  text-decoration: none;
                }

                a:not(.ant-pagination a):hover {
                  color: ${baseTheme.colorLinkHover} !important;
                }

                a:not(.ant-pagination a):active {
                  color: ${baseTheme.colorLinkActive} !important;
                }
              `}
            />
            {/*</Watermark>*/}
            <SimpleLoadingApp />
            <PwaInstallPrompt />
            <div className="App">
              <HelmetProvider>
                <BrowserRouter basename={basePath || undefined}>
                  <ThemeSwitcherProvider
                    themeMap={themes}
                    defaultTheme={currentTheme || 'light'}
                    insertionPoint="styles-insertion-point"
                  >
                    <Helmet>
                      <title>{appName}</title>
                      <link rel="icon" type="image/png" href={`/apps/${appId}/favicon.ico`} />
                      <link
                        rel="apple-touch-icon"
                        sizes="180x180"
                        href={`/apps/${appId}/apple-touch-icon.png`}
                      />
                      <link
                        rel="icon"
                        type="image/png"
                        sizes="32x32"
                        href={`/apps/${appId}/favicon-32x32.png`}
                      />
                      <link
                        rel="icon"
                        type="image/png"
                        sizes="16x16"
                        href={`/apps/${appId}/favicon-16x16.png`}
                      />
                      <link rel="manifest" href={manifestUrl} />
                      <link
                        rel="mask-icon"
                        href={`/apps/${appId}/safari-pinned-tab.svg`}
                        color="#5bbad5"
                      />
                    </Helmet>
                    <Layouts />
                    {storedToken && <BottomDrawer />}
                  </ThemeSwitcherProvider>
                </BrowserRouter>
              </HelmetProvider>
            </div>
          </AntdApp>
        </ConfigProvider>
      </languageContextReload.Provider>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default App;

// *******************************************************************************
// HELPERS
// *******************************************************************************
